import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useCollectionBinding,
  EditableSlotGroup,
  EditableCollectionBinding,
  RichTextContent,
} from '@vibesites/runtime/react';
import { cn, VibesitesImage } from '@vibesites/ui-primitives';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useSecondaryTextClass } from '../hooks/useSecondaryTextClass';
import { ctaLinkProps } from '../utils/scheduleTourModal';

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://media.multihub.io/company/5767906449162250/01a0e8db-fbbe-73d2-bd27-ec9bdde5db0e.webp',
    },
  },
};

const galleryCtaDef: SlotGroupDefinition = {
  label: 'Browse Gallery CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Label', type: 'text', defaultValue: 'BROWSE GALLERY' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const amenitiesCtaDef: SlotGroupDefinition = {
  label: 'View Amenities CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Label', type: 'text', defaultValue: 'VIEW AMENITIES' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

import { btnOnLight } from '../utils/buttonStyles';
import {
  FLOORPLAN_CATEGORY_CARD_LIMIT,
  selectCategoryCardPhotos,
} from './FloorplansList';

const btnClassName =
  `inline-flex items-center justify-center w-full whitespace-nowrap px-8 py-3.5 font-body text-sm font-medium tracking-[0.18em] uppercase ${btnOnLight}`;
const GALLERY_LIMIT = 6;
/** Slider slots plus the photos the floor-plan bedroom cards already display. */
const APARTMENT_FETCH_LIMIT = GALLERY_LIMIT + FLOORPLAN_CATEGORY_CARD_LIMIT;

interface GalleryImage {
  id?: string | null;
  image?: string | null;
  original_url?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
}

function resolveSrc(photo: GalleryImage): string {
  return photo.image ?? photo.original_url ?? '';
}

/** Ids and URLs for photos already shown on the floor-plan bedroom cards. */
function reservedPhotoKeys(photos: GalleryImage[]): Set<string> {
  const keys = new Set<string>();
  for (const photo of photos) {
    const id = photo.id?.trim();
    if (id && id !== 'false') keys.add(`id:${id}`);
    const image = photo.image?.trim();
    const original = photo.original_url?.trim();
    if (image) keys.add(`url:${image}`);
    if (original) keys.add(`url:${original}`);
  }
  return keys;
}

function isReservedPhoto(photo: GalleryImage, reserved: Set<string>): boolean {
  const id = photo.id?.trim();
  if (id && id !== 'false' && reserved.has(`id:${id}`)) return true;
  const src = resolveSrc(photo).trim();
  return !!src && reserved.has(`url:${src}`);
}

/** Interleave apartment + community photos into a balanced mix of up to `limit`. */
function mixGalleryPhotos(
  apartments: GalleryImage[],
  community: GalleryImage[],
  limit: number = GALLERY_LIMIT,
): GalleryImage[] {
  const apt = apartments.filter((p) => resolveSrc(p));
  const com = community.filter((p) => resolveSrc(p));
  const mixed: GalleryImage[] = [];
  let i = 0;
  let j = 0;

  while (mixed.length < limit && (i < apt.length || j < com.length)) {
    if (i < apt.length && mixed.length < limit) mixed.push(apt[i++]);
    if (j < com.length && mixed.length < limit) mixed.push(com[j++]);
  }

  return mixed;
}

function usePerPage(): number {
  const [perPage, setPerPage] = useState(3);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w < 640) setPerPage(1);
      else if (w < 1024) setPerPage(2);
      else setPerPage(3);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return perPage;
}

function chunk<T>(items: T[], size: number): T[][] {
  const pages: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    pages.push(items.slice(i, i + size));
  }
  return pages.length > 0 ? pages : [[]];
}

function GalleryLightbox({
  images,
  selectedIndex,
  onClose,
  onChange,
}: {
  images: GalleryImage[];
  selectedIndex: number | null;
  onClose: () => void;
  onChange: (index: number | null) => void;
}) {
  const total = images.length;

  const goNext = useCallback(() => {
    if (selectedIndex === null || total === 0) return;
    onChange((selectedIndex + 1) % total);
  }, [onChange, selectedIndex, total]);

  const goPrev = useCallback(() => {
    if (selectedIndex === null || total === 0) return;
    onChange((selectedIndex - 1 + total) % total);
  }, [onChange, selectedIndex, total]);

  useEffect(() => {
    if (selectedIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prev;
    };
  }, [selectedIndex, goNext, goPrev, onClose]);

  if (selectedIndex === null) return null;

  const photo = images[selectedIndex];
  const src = resolveSrc(photo);
  const alt = photo.alt_text ?? photo.title ?? 'Property photo';

  return createPortal(
    <div
      className="lifestyleintro-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Photo gallery"
      onClick={onClose}
    >
      <button
        type="button"
        className="lifestyleintro-lightbox__close"
        onClick={onClose}
        aria-label="Close gallery"
      >
        <X size={28} strokeWidth={1.5} />
      </button>

      {total > 1 && (
        <>
          <button
            type="button"
            className="lifestyleintro-lightbox__arrow lifestyleintro-lightbox__arrow--prev"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            aria-label="Previous photo"
          >
            <ChevronLeft size={36} strokeWidth={1.5} />
          </button>
          <button
            type="button"
            className="lifestyleintro-lightbox__arrow lifestyleintro-lightbox__arrow--next"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            aria-label="Next photo"
          >
            <ChevronRight size={36} strokeWidth={1.5} />
          </button>
        </>
      )}

      <div
        className="lifestyleintro-lightbox__figure"
        onClick={(e) => e.stopPropagation()}
      >
        {src ? (
          <VibesitesImage
            url={src}
            alt={alt}
            sizes="90vw"
            className="lifestyleintro-lightbox__img"
          />
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

function LifestyleGallery() {
  const apartmentImages = useCollectionBinding<GalleryImage>(
    'lifestyleintro.apartment_images',
    {
      collection: 'photo_gallery',
      filter: { title: 'Apartment' },
      defaultLimit: APARTMENT_FETCH_LIMIT,
    },
  );
  const communityImages = useCollectionBinding<GalleryImage>(
    'lifestyleintro.community_images',
    {
      collection: 'photo_gallery',
      filter: { title: 'Community' },
      defaultLimit: GALLERY_LIMIT,
    },
  );
  // Same bindings the bedroom cards resolve, so editor reorders stay in sync.
  const categoryCardPhotos = useCollectionBinding<GalleryImage>(
    'floorplanslist.apartment_images',
    {
      collection: 'photo_gallery',
      filter: { title: 'Apartment' },
      defaultLimit: FLOORPLAN_CATEGORY_CARD_LIMIT,
    },
  );
  const floorplans = useCollectionBinding<{ floorplan_beds_min?: string | null }>(
    'floorplanslist.items',
    { collection: 'floorplans' },
  );

  const perPage = usePerPage();
  const [page, setPage] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const touchStartX = useRef<number | null>(null);

  const reservedSrcs = useMemo(
    () => reservedPhotoKeys(selectCategoryCardPhotos(categoryCardPhotos, floorplans)),
    [categoryCardPhotos, floorplans],
  );

  const photos = useMemo(() => {
    const apartments = (apartmentImages ?? []).filter(
      (photo) => !isReservedPhoto(photo, reservedSrcs),
    );
    const community = (communityImages ?? []).filter(
      (photo) => !isReservedPhoto(photo, reservedSrcs),
    );
    return mixGalleryPhotos(apartments, community, GALLERY_LIMIT);
  }, [apartmentImages, communityImages, reservedSrcs]);

  const pages = useMemo(() => chunk(photos, perPage), [photos, perPage]);
  const pageCount = pages.length;

  useEffect(() => {
    setPage((p) => Math.min(p, Math.max(0, pageCount - 1)));
  }, [pageCount]);

  const goTo = useCallback(
    (next: number) => {
      if (pageCount <= 1) return;
      setPage(((next % pageCount) + pageCount) % pageCount);
    },
    [pageCount],
  );

  const goPrev = useCallback(() => goTo(page - 1), [goTo, page]);
  const goNext = useCallback(() => goTo(page + 1), [goTo, page]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null) return;
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      touchStartX.current = null;
      if (Math.abs(dx) < 40) return;
      if (dx < 0) goNext();
      else goPrev();
    },
    [goNext, goPrev],
  );

  if (photos.length === 0) {
    return (
      <div className="lifestyleintro-gallery mt-10 md:mt-14 px-6 sm:px-8 lg:px-10 text-center">
        <EditableCollectionBinding
          bindingPath="lifestyleintro.apartment_images"
          label="Apartment Photos"
        >
          <EditableCollectionBinding
            bindingPath="lifestyleintro.community_images"
            label="Community Photos"
          >
            <p className="font-body text-muted-foreground">No photos available.</p>
          </EditableCollectionBinding>
        </EditableCollectionBinding>
      </div>
    );
  }

  return (
    <div className="lifestyleintro-gallery mt-10 md:mt-14" aria-label="Photo gallery">
      <EditableCollectionBinding
        bindingPath="lifestyleintro.apartment_images"
        label="Apartment Photos"
      >
        <EditableCollectionBinding
          bindingPath="lifestyleintro.community_images"
          label="Community Photos"
        >
          <div className="lifestyleintro-gallery__shell mx-auto w-full px-6 sm:px-8 lg:px-10">
            <div className="lifestyleintro-gallery__stage relative">
              <div
                className="lifestyleintro-gallery__viewport overflow-hidden"
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                <div
                  className="lifestyleintro-gallery__track flex"
                  style={{ transform: `translateX(-${page * 100}%)` }}
                >
                  {pages.map((pagePhotos, pageIndex) => (
                    <div
                      key={pageIndex}
                      className="lifestyleintro-gallery__page flex w-full shrink-0 gap-3 sm:gap-4"
                      aria-hidden={pageIndex === page ? undefined : true}
                    >
                      {pagePhotos.map((photo, i) => {
                        const globalIndex = pageIndex * perPage + i;
                        const src = resolveSrc(photo);
                        const alt = photo.alt_text ?? photo.title ?? 'Property photo';

                        return (
                          <button
                            key={src + globalIndex}
                            type="button"
                            className="lifestyleintro-gallery__item group relative min-w-0 flex-1 overflow-hidden cursor-pointer border-0 bg-transparent p-0"
                            onClick={() => setSelectedIndex(globalIndex)}
                            aria-label={`View photo ${globalIndex + 1}: ${alt}`}
                            tabIndex={pageIndex === page ? 0 : -1}
                          >
                            <VibesitesImage
                              url={src}
                              alt={alt}
                              sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                              className="lifestyleintro-gallery__img block h-full w-full object-cover aspect-[4/3]"
                            />
                            <span className="lifestyleintro-gallery__hover-overlay" aria-hidden="true" />
                          </button>
                        );
                      })}
                      {pagePhotos.length < perPage &&
                        Array.from({ length: perPage - pagePhotos.length }, (_, i) => (
                          <div key={`pad-${i}`} className="min-w-0 flex-1" aria-hidden="true" />
                        ))}
                    </div>
                  ))}
                </div>
              </div>

              {pageCount > 1 && (
                <>
                  <button
                    type="button"
                    className="lifestyleintro-gallery__arrow lifestyleintro-gallery__arrow--prev"
                    onClick={goPrev}
                    aria-label="Previous photos"
                  >
                    <ChevronLeft size={28} strokeWidth={1.25} />
                  </button>
                  <button
                    type="button"
                    className="lifestyleintro-gallery__arrow lifestyleintro-gallery__arrow--next"
                    onClick={goNext}
                    aria-label="Next photos"
                  >
                    <ChevronRight size={28} strokeWidth={1.25} />
                  </button>
                </>
              )}
            </div>

            {pageCount > 1 && (
              <div className="lifestyleintro-gallery__dots" role="tablist" aria-label="Gallery pages">
                {pages.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    role="tab"
                    aria-selected={i === page}
                    aria-label={`Show photos page ${i + 1}`}
                    className={`lifestyleintro-gallery__dot${i === page ? ' is-active' : ''}`}
                    onClick={() => setPage(i)}
                  />
                ))}
              </div>
            )}
          </div>
        </EditableCollectionBinding>
      </EditableCollectionBinding>

      <GalleryLightbox
        images={photos}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <style>{`
        .lifestyleintro-gallery__stage {
          position: relative;
        }

        .lifestyleintro-gallery__track {
          transition: transform 0.55s cubic-bezier(0.4, 0, 0.2, 1);
          will-change: transform;
        }

        @media (prefers-reduced-motion: reduce) {
          .lifestyleintro-gallery__track {
            transition: none;
          }
        }

        .lifestyleintro-gallery__img {
          display: block;
          border-radius: 0;
        }

        .lifestyleintro-gallery__hover-overlay {
          position: absolute;
          inset: 0;
          background: #000;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.25s ease;
        }

        .lifestyleintro-gallery__item:hover .lifestyleintro-gallery__hover-overlay,
        .lifestyleintro-gallery__item:focus-visible .lifestyleintro-gallery__hover-overlay {
          opacity: 0.5;
        }

        .lifestyleintro-gallery__arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 2.5rem;
          height: 2.5rem;
          padding: 0;
          border: none;
          background: transparent;
          color: color-mix(in srgb, var(--vibe-color-text) 45%, transparent);
          cursor: pointer;
          transition: color 0.2s ease;
        }

        .lifestyleintro-gallery__arrow:hover {
          color: var(--vibe-color-text);
        }

        .lifestyleintro-gallery__arrow--prev {
          left: 0;
          transform: translate(-100%, -50%);
        }

        .lifestyleintro-gallery__arrow--next {
          right: 0;
          transform: translate(100%, -50%);
        }

        .lifestyleintro-gallery__dots {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.55rem;
          margin-top: 1.25rem;
        }

        .lifestyleintro-gallery__dot {
          width: 0.5rem;
          height: 0.5rem;
          padding: 0;
          border: none;
          border-radius: 9999px;
          background: color-mix(in srgb, var(--vibe-color-text) 25%, transparent);
          cursor: pointer;
          transition: background 0.2s ease, transform 0.2s ease;
        }

        .lifestyleintro-gallery__dot:hover {
          background: color-mix(in srgb, var(--vibe-color-text) 45%, transparent);
        }

        .lifestyleintro-gallery__dot.is-active {
          background: color-mix(in srgb, var(--vibe-color-text) 75%, transparent);
          transform: scale(1.15);
        }

        .lifestyleintro-lightbox {
          position: fixed;
          inset: 0;
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          background: color-mix(in srgb, var(--vibe-color-text) 88%, transparent);
          padding: 1.5rem;
        }

        .lifestyleintro-lightbox__figure {
          max-width: min(1100px, 92vw);
          max-height: 88vh;
        }

        .lifestyleintro-lightbox__img {
          max-height: 88vh;
          width: auto;
          max-width: 100%;
          height: auto;
          object-fit: contain;
        }

        .lifestyleintro-lightbox__close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 2.75rem;
          height: 2.75rem;
          border: none;
          background: transparent;
          color: var(--vibe-color-background);
          cursor: pointer;
        }

        .lifestyleintro-lightbox__arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 3rem;
          height: 3rem;
          border: none;
          background: transparent;
          color: var(--vibe-color-background);
          cursor: pointer;
        }

        .lifestyleintro-lightbox__arrow--prev { left: 0.75rem; }
        .lifestyleintro-lightbox__arrow--next { right: 0.75rem; }
      `}</style>
    </div>
  );
}

export function LifestyleIntro() {
  const bg = useSlotGroup('lifestyleintro.bg_image', bgImageDef);
  const galleryCta = useSlotGroup('lifestyleintro.gallery_cta', galleryCtaDef);
  const amenitiesCta = useSlotGroup('lifestyleintro.amenities_cta', amenitiesCtaDef);
  const secondaryTextClass = useSecondaryTextClass();

  return (
    <section className="lifestyle-intro relative overflow-hidden py-16 md:py-24 px-4 sm:px-6 bg-background">
      <div className="absolute inset-0 z-0">
        <EditableSlotGroup groupPath="lifestyleintro.bg_image" definition={bgImageDef}>
          <VibesitesImage
            url={
              bg.image ||
              'https://media.multihub.io/company/5767906449162250/01a0e8db-fbbe-73d2-bd27-ec9bdde5db0e.webp'
            }
            alt=""
            sizes="100vw"
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
        </EditableSlotGroup>
      </div>
      <div className="absolute inset-0 z-[1] bg-white/85" aria-hidden />

      <div className="relative z-[2] mx-auto max-w-4xl text-center">
        <RichTextContent
          path="lifestyleintro.headline"
          as="h3"
          className={cn(
            'font-body text-2xl sm:text-3xl md:text-4xl lg:text-[2.75rem] font-medium tracking-[0.12em] uppercase leading-tight',
            secondaryTextClass,
          )}
        />

        <RichTextContent
          path="lifestyleintro.body"
          as="div"
          className="font-body mt-8 md:mt-10 mx-auto max-w-2xl text-sm sm:text-base text-foreground leading-relaxed [&_a]:text-primary [&_a]:no-underline hover:[&_a]:underline"
        />

        <div className="mt-10 md:mt-12 mx-auto grid w-full max-w-xs sm:w-fit sm:max-w-none grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          <EditableSlotGroup groupPath="lifestyleintro.gallery_cta" definition={galleryCtaDef}>
            <a
              {...ctaLinkProps(galleryCta.url || '/gallery', galleryCta._target)}
              className={btnClassName}
            >
              {galleryCta.label || 'BROWSE GALLERY'}
            </a>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="lifestyleintro.amenities_cta" definition={amenitiesCtaDef}>
            <a
              {...ctaLinkProps(amenitiesCta.url || '/amenities', amenitiesCta._target)}
              className={btnClassName}
            >
              {amenitiesCta.label || 'VIEW AMENITIES'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <div className="relative z-[2]">
        <LifestyleGallery />
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'LifestyleIntro',
  displayName: 'Lifestyle Intro',
  tokens: {
    'lifestyleintro.headline': {
      label: 'Headline',
      type: 'richtext',
      defaultValue: '<p>A LIFESTYLE DESIGNED FOR YOU</p>',
    },
    'lifestyleintro.body': {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>Your <a href="/amenities">apartment-living experience</a> is our top priority at <span data-type="field-token" data-field="property_name"></span>. Our exclusive amenities package is one of the best in <span data-type="field-token" data-field="property_address_city"></span>, with apartment features like in-home washers/dryers, oversized windows for ample sunlight, and designer finishes. You\'ll never have to worry about incoming packages with our 24/7 in-building package acceptance via our package rooms, located in each building. Have to jump on a work call? Our wifi-equipped work-from-home space is the perfect place to close the deal. We\'re also a pet-friendly community, so bring your furry friends and enjoy our dog wash center! At our apartments for rent in <span data-type="field-token" data-field="property_address_city"></span>, we have everything you need at home.</p>',
      generator: {
        prompt:
          'Write a warm, resident-focused lifestyle paragraph highlighting this property\'s standout apartment features and community amenities. Open with a phrase like "apartment-living experience" linked to /amenities. Mention the property name and city naturally. Cover 3–5 real amenities or finishes that exist for this property (e.g. in-unit laundry, package rooms, WFH spaces, pet amenities) without inventing features that are not present. Close by reinforcing that residents have everything they need at home.',
        context:
          'Centered intro section on the Home page below the apply CTA banner. Sits between a blue uppercase headline ("A LIFESTYLE DESIGNED FOR YOU") and two outline buttons (Browse Gallery, View Amenities), with a multi-image photo gallery slider directly below the CTAs.',
        length: '1 paragraph, 80–120 words',
      },
    },
  },
  slotGroups: {
    'lifestyleintro.bg_image': bgImageDef,
    'lifestyleintro.gallery_cta': galleryCtaDef,
    'lifestyleintro.amenities_cta': amenitiesCtaDef,
  },
  collectionBindings: {
    'lifestyleintro.apartment_images': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
      filter: { title: 'Apartment' },
      defaultLimit: 10,
      description:
        'Apartment gallery photos mixed into the lifestyle intro slider (up to 6 total with community). Photos already shown on the floor-plan bedroom cards are skipped; extras are loaded so the slider can still fill.',
    },
    'lifestyleintro.community_images': {
      label: 'Community Photos',
      collection: 'photo_gallery',
      filter: { title: 'Community' },
      defaultLimit: 6,
      description:
        'Community gallery photos mixed into the lifestyle intro slider (up to 6 total with apartment).',
    },
  },
};

(LifestyleIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default LifestyleIntro;
